import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import styles from "./Toast.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const ToastCtx = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const tm = timersRef.current.get(id);
    if (tm) {
      clearTimeout(tm);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback((type, message, duration = 3000, color = null) => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, type, message, color }]);
    const tm = setTimeout(() => remove(id), duration);
    timersRef.current.set(id, tm);
    return id;
  }, [remove]);

  const api = useMemo(() => ({
    success: (msg, ms, color) => push("success", msg, ms, color),
    error: (msg, ms, color) => push("error", msg, ms, color),
    info: (msg, ms, color) => push("info", msg, ms, color),
    warning: (msg, ms, color) => push("warning", msg, ms, color),
    remove,
  }), [push, remove]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className={styles.container} aria-live="polite" aria-atomic="true">
        {toasts.map((t) => {
          let bgStyle = {};
          if (t.color) {
            // Custom color: use it as the base
            bgStyle = { background: `linear-gradient(145deg, ${t.color}, ${t.color}cc)` };
          }
          return (
            <div
              key={t.id}
              className={`${styles.toast} ${styles[t.type]}`}
              role={t.type === "error" ? "alert" : "status"}
              style={t.color ? bgStyle : undefined}
            >
              <span className={styles.message}>{t.message}</span>
              <button className={styles.close} aria-label="Fechar" onClick={() => remove(t.id)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
